import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CircularProgress, MenuItem, Select, Tooltip } from "@mui/material";
import { useResponseTimeTrend } from "hooks/useResponseTimeTrend";
import { useBranches } from "hooks/useBranches";
import { ResponseTimeMetric, ResponseTimePoint } from "interfaces/entities/ticketStats";
import moment from "moment";

const COLOR_RESOLUTION = "#4F46E5"; // фиолетовый — время решения
const COLOR_FIRST_RESPONSE = "#22C55E"; // зелёный — время первого ответа

const WINDOW_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 7, label: "7 дней" },
  { value: 30, label: "30 дней" },
  { value: 90, label: "90 дней" },
];

// Человекочитаемое представление длительности.
// Стараемся показать «чистую» единицу — час или день — без лишних деталей.
const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(0)} мин`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)} ч`;
  const days = hours / 24;
  return `${days.toFixed(1)} д`;
};

// Кратное представление для оси Y графика — в часах для удобства чтения.
const secondsToHours = (s: number) => s / 3600;

const Metric = ({
  title,
  color,
  metric,
  hint,
}: {
  title: string;
  color: string;
  metric: ResponseTimeMetric;
  hint: string;
}) => (
  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: 999,
        background: color,
        marginTop: 8,
        flexShrink: 0,
      }}
    />
    <div>
      <div style={{ fontSize: 12, color: "#64748B" }}>
        <Tooltip title={hint} arrow>
          <span>{title}</span>
        </Tooltip>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
        {formatDuration(metric.avgSeconds)}
      </div>
      <div style={{ fontSize: 11, color: "#94A3B8" }}>
        {metric.ticketsCount} {metric.ticketsCount === 1 ? "тикет" : "тикетов"}
      </div>
    </div>
  </div>
);

export interface TicketsResponseTimeTrendProps {
  /** Длина окна по умолчанию. */
  defaultWindowDays?: number;
  branchID?: number | string;
}

/**
 * Виджет «Среднее время решения и первого ответа».
 * Линейный график с двумя метриками + сводка за период:
 *  - Время решения = closedAt - createdAt
 *  - Первый ответ  = первое не-внутреннее сообщение сотрудника - createdAt
 */
export const TicketsResponseTimeTrend = ({
  defaultWindowDays = 30,
  branchID,
}: TicketsResponseTimeTrendProps) => {
  const [windowDays, setWindowDays] = useState<number>(defaultWindowDays);
  const [localBranchID, setLocalBranchID] = useState<number | "">("");
  const { data: branches = [] } = useBranches();
  const activeBranchID = localBranchID !== "" ? localBranchID : branchID;

  const { data, isLoading, error } = useResponseTimeTrend({
    windowDays,
    ...(activeBranchID ? { branchID: activeBranchID } : {}),
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<any>(null);

  const points = useMemo(() => {
    if (!data) return [] as ResponseTimePoint[];
    return data.resolution.series; // оба series одной длины — берём из resolution для labels
  }, [data]);

  useEffect(() => {
    const ChartCtor = (window as any).Chart;
    if (!data || !canvasRef.current || !ChartCtor) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const labels = data.resolution.series.map((p) => moment(p.day).format("DD MMM"));
    const resolutionData = data.resolution.series.map((p) =>
      // 0 показываем как null, чтобы линия не «падала в пол» на пустых днях
      p.count > 0 ? secondsToHours(p.avgSeconds) : null,
    );
    const firstResponseData = data.firstResponse.series.map((p) =>
      p.count > 0 ? secondsToHours(p.avgSeconds) : null,
    );

    chartRef.current = new ChartCtor(canvasRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Время решения",
            data: resolutionData,
            borderColor: COLOR_RESOLUTION,
            backgroundColor: "transparent",
            tension: 0.3,
            spanGaps: true,
            pointRadius: 2,
            pointHoverRadius: 5,
            borderWidth: 2,
          },
          {
            label: "Первый ответ",
            data: firstResponseData,
            borderColor: COLOR_FIRST_RESPONSE,
            backgroundColor: "transparent",
            tension: 0.3,
            spanGaps: true,
            pointRadius: 2,
            pointHoverRadius: 5,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: {
          legend: {
            display: true,
            position: "bottom",
          },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const idx = ctx.dataIndex;
                const isResolution = ctx.datasetIndex === 0;
                const series = isResolution
                  ? data.resolution.series[idx]
                  : data.firstResponse.series[idx];
                if (!series || series.count === 0) {
                  return ` ${ctx.dataset.label}: нет данных`;
                }
                return ` ${ctx.dataset.label}: ${formatDuration(
                  series.avgSeconds,
                )} (${series.count} тик.)`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
            },
          },
          y: {
            beginAtZero: true,
            grid: { color: "#F1F5F9" },
            ticks: {
              callback: (value: any) => `${value}ч`,
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data]);

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
              Время решения и первого ответа
            </h4>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
              Среднее по дням за {windowDays} дн.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Select
              size="small"
              value={localBranchID}
              onChange={(e) => setLocalBranchID(e.target.value as number | "")}
              displayEmpty
              style={{ minWidth: 160 }}
            >
              <MenuItem value="">Все филиалы</MenuItem>
              {branches?.data?.map((b: any) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
            >
              {WINDOW_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {isLoading && !data && (
          <div style={{ textAlign: "center", padding: 48 }}>
            <CircularProgress size={24} />
          </div>
        )}

        {!isLoading && error && (
          <div
            style={{
              textAlign: "center",
              color: "#94A3B8",
              padding: 32,
            }}
          >
            Не удалось загрузить статистику времени ответа
          </div>
        )}

        {data && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 24,
                marginBottom: 24,
              }}
            >
              <Metric
                title="Среднее время решения"
                color={COLOR_RESOLUTION}
                metric={data.resolution}
                hint="Сколько в среднем проходит от создания тикета до его закрытия (closedAt − createdAt)."
              />
              <Metric
                title="Среднее время первого ответа"
                color={COLOR_FIRST_RESPONSE}
                metric={data.firstResponse}
                hint="От создания тикета до первого публичного ответа сотрудника (без учёта внутренних заметок)."
              />
            </div>

            {data.resolution.ticketsCount === 0 && data.firstResponse.ticketsCount === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#94A3B8",
                  padding: 32,
                }}
              >
                За выбранный период нет данных
              </div>
            ) : (
              <div style={{ height: 280, position: "relative" }}>
                <canvas ref={canvasRef} />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TicketsResponseTimeTrend;
